<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatDisplayDate, formatDisplayDateTime } from '@yuksalish/ui'
import { useApiClient, formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'

import ProductCostDraftEditor from '../components/ProductCostDraftEditor.vue'
import OverheadPickerModal from '../components/OverheadPickerModal.vue'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import type { CurrencyOption, ExpenseReference, OverheadRule, OverheadRulePayload, ProductOption } from '../types/finance'
import { formatMoneyValue, formatQuantity } from '../utils/format'
import { compareLines, comparable, displayCostElement, isKnittingMaterial, knittingOperationSequences, type CostRow as Row, type CostVersion as Version } from '../utils/productCostWorkspace'
const { t } = useI18n()
const client = useApiClient()
const auth = useAuthStore()
const can = (permission: string) => auth.user?.permissions.includes(permission) ?? false
const versions = ref<Version[]>([])
const selected = ref<Version | null>(null)
const busy = ref(false)
const error = ref('')
const sources = ref<Row | null>(null)
const operatorPrices = ref<Record<string,string>>({})
const operatorRates = ref<Record<string,string>>({})
const { listOverheadRules, createOverheadRule, approveOverheadRule, listCurrencies } = useFinanceApi()
const overheadRules = ref<OverheadRule[]>([])
const currencies = ref<CurrencyOption[]>([])
const overheadsLoading = ref(false)
const overheadSaving = ref(false)
const overheadError = ref('')
const overheadOpen = ref(false)
const overheadForm = ref({
  code:'', name:'', allocation_basis:'prime_cost_percent', rate_type:'percent' as 'percent'|'amount',
  rate_value:'', currency_id:'', apply_to_product:true, effective_from:'', effective_to:'', priority:'0',
  approve:false,
})
const expensePicker = ref(false)
const selectedExpense = ref<ExpenseReference | null>(null)
const bomMaterials = computed<Row[]>(() => (sources.value?.materials as Row[] | undefined) ?? [])
const bomOperations = computed<Row[]>(() => (sources.value?.operations as Row[] | undefined) ?? [])
const comparison = ref<{left:Version;right:Version;unit_cost_delta:string} | null>(null)
const compareId = ref('')
const tab = ref('knitting')
const offset = ref(0)
const filter = ref('')
const filterName = ref('')
const createOpen = ref(false)
const editorMode=ref<'create'|'edit'|'clone'>('create')
const editorVersion=ref<Version|null>(null)
const filterPicker=ref(false)
const filterLabel=computed(()=>filterName.value || t('finance.productCost.allProducts'))
/** The detail endpoint does not join the product name, so fall back to the loaded list. */
const versionNames=computed<Record<number,string>>(()=>Object.fromEntries(
  versions.value.filter(version=>version.product_name).map(version=>[version.id,String(version.product_name)]),
))
const selectedProductName=computed(()=>{
  if(!selected.value) return ''
  return String(selected.value.product_name ?? versionNames.value[selected.value.id] ?? '')
})
const comparisonOptions=ref<Version[]>([])
const comparisonRows=computed(()=>comparison.value ? compareLines(comparison.value.left.lines??[],comparison.value.right.lines??[]) : [])
const knittingSequences=computed(()=>knittingOperationSequences(bomOperations.value,bomMaterials.value))
const bomMaterialIndex=computed(()=>new Map(bomMaterials.value.map(row=>[String(row.source_id),row])))
const elementOf=(line:Row)=>displayCostElement(line,bomMaterialIndex.value,knittingSequences.value)
/** True when the API grouped knitting materials under another element. */
const serverMissingKnitting=computed(()=>
  bomMaterials.value.some(row=>isKnittingMaterial(row,knittingSequences.value)) &&
  !(selected.value?.totals ?? []).some(total=>String(total.cost_element)==='direct_material_knitting')
)
const issues=computed<(Row & {blocking:boolean})[]>(()=>[
  ...((sources.value?.blocking_errors??[]) as Row[]).map(row=>({...row,blocking:true})),
  ...((sources.value?.warnings??[]) as Row[]).map(row=>({...row,blocking:false})),
])
/** Missing optional keys must not leak the full key path to the user. */
function messageOr(key:string, fallback:string):string {
  const label = t(key)
  return label===key ? fallback : label
}
function statusLabel(status:string):string {
  return messageOr(`finance.productCost.status.${status}`, status)
}
function guidanceFor(code:string):string {
  return messageOr(`finance.productCost.guidance.${code}`, t('finance.productCost.guidanceFallback'))
}
function pricingMethodLabel(method:unknown):string {
  const value=String(method ?? '')
  return t(value==='markup' ? 'finance.productCost.pricing.markupShort' : value==='target_margin' ? 'finance.productCost.pricing.marginShort' : 'finance.productCost.pricing.manualShort')
}
const elementKeys:Record<string,string>={
  direct_material_knitting:'elementKnitting',direct_material_other:'elementOtherMaterial',packaging_material:'elementPackaging',
  direct_labor:'elementLabour',machine:'elementMachine',subcontracting:'elementSubcontracting',
  variable_overhead:'elementVariableOverhead',fixed_overhead:'elementFixedOverhead',
  quality_and_scrap:'elementQuality',other_direct_cost:'elementOtherCost',
}
function costElementLabel(element:unknown):string {
  const code=String(element ?? '')
  const key=elementKeys[code]
  return key ? messageOr(`finance.productCost.${key}`, code) : code
}
/** The API gives a purchase price priority over an operator price; say so instead of hiding it. */
function priceOverrideNotice(row:Row):string {
  const typed=String(operatorPrices.value[String(row.material_id)] ?? '').trim()
  const price=priceOf(row)
  if(typed==='' || !price) return ''
  const source=String(price.source_type ?? '')
  if(source==='product_purchase_price') return t('finance.productCost.priceOverrideIgnored', { price: formatMoneyValue(price.unit_rate), currency: String(price.currency_code ?? '') })
  if(source==='operator_input' && Number(typed)!==Number(price.unit_rate)) return t('finance.productCost.priceAppliedDifferent', { applied: formatMoneyValue(price.unit_rate), typed: formatMoneyValue(typed) })
  return ''
}
const auditFields = [
  {field:'created_by_user_id',key:'createdByUserId'},
  {field:'created_at',key:'createdAt'},
  {field:'calculated_by_user_id',key:'calculatedByUserId'},
  {field:'calculated_at',key:'calculatedAt'},
  {field:'submitted_by_user_id',key:'submittedByUserId'},
  {field:'submitted_at',key:'submittedAt'},
  {field:'approved_by_user_id',key:'approvedByUserId'},
  {field:'approved_at',key:'approvedAt'},
  {field:'published_by_user_id',key:'publishedByUserId'},
  {field:'published_at',key:'publishedAt'},
  {field:'row_version',key:'rowVersion'},
  {field:'calculation_checksum',key:'calculationChecksum'},
]
function beginEditor(mode:'create'|'edit'|'clone') {
  editorMode.value=mode
  const current=selected.value
  editorVersion.value=mode==='create' || !current ? null : {...current, product_name:selectedProductName.value}
  createOpen.value=true
}
async function saved(version:Version) {
  selected.value=version;createOpen.value=false;comparison.value=null;compareId.value=''
  await run(async()=>{await load();await loadComparisonOptions(version);await fetchSources(version);await loadOverheadRules()})
}
async function chooseFilter(product:ProductOption) {
  filterPicker.value=false;filter.value=String(product.id);filterName.value=product.name
  offset.value=0;selected.value=null;comparison.value=null;sources.value=null
  await run(load)
}
async function loadComparisonOptions(version:Version) {
  comparisonOptions.value=[];compareId.value=''
  const response=await client.get<{data:Version[]}>('/product-costs',{params:{product_id:version.product_id,limit:200}})
  comparisonOptions.value=response.data.data.filter(other=>other.id!==version.id && comparable(version,other))
}
const tabs = computed(() => [
  {id:'knitting',label:t('finance.productCost.tab.knitting'),badge:0},
  {id:'materials',label:t('finance.productCost.tab.materials'),badge:0},
  {id:'operations',label:t('finance.productCost.tab.operations'),badge:0},
  {id:'expenses',label:t('finance.productCost.tab.expenses'),badge:0},
  {id:'warnings',label:t('finance.productCost.tab.warnings'),badge:issues.value.length},
  {id:'summary',label:t('finance.productCost.tab.summary'),badge:0},
  {id:'audit',label:t('finance.productCost.tab.audit'),badge:0},
])
const rows = computed(() => (selected.value?.lines ?? []).filter(line => {
  const element = elementOf(line)
  if(tab.value==='knitting') return element==='direct_material_knitting'
  if(tab.value==='materials') return ['direct_material_other','packaging_material'].includes(element)
  if(tab.value==='operations') return ['direct_labor','machine','subcontracting'].includes(element)
  return !['direct_material_knitting','direct_material_other','packaging_material','direct_labor','machine','subcontracting'].includes(element)
}))
const actions = computed(() => {
  const status = selected.value?.status
  return [
    {id:'calculate',permission:'product_costs.calculate',allowed:['draft','calculated']},
    {id:'submit',permission:'product_costs.submit',allowed:['calculated']},
    {id:'approve',permission:'product_costs.approve',allowed:['pending_approval']},
    {id:'reject',permission:'product_costs.approve',allowed:['pending_approval']},
    {id:'publish',permission:'product_costs.publish',allowed:['approved']},
  ]
    .filter(action => action.allowed.includes(status ?? '') && can(action.permission))
    .map(action => ({...action,label:t(`finance.productCost.action.${action.id}`)}))
})
// Retain the same key after a failed response so the user can safely retry.
const retryKeys = new Map<string,string>()
const pricingMethod=ref('markup')
const pricingValue=ref('0')
const show = (value: unknown) => value == null ? '—' : typeof value==='object' ? JSON.stringify(value,null,2) : String(value)
const priceOf = (row:Row) => (row.price as Row | null | undefined) ?? null
const uomOf = (row:Row) => (row.base_uom as Row | null | undefined) ?? null
const rateOf = (row:Row) => (row.rate as Row | null | undefined) ?? null
function priceSourceLabel(row:Row):string {
  const price = priceOf(row)
  if(!price) return t('finance.productCost.priceMissing')
  const type = String(price.source_type ?? '')
  if(type==='product_purchase_price') return String(price.source_policy ?? '')==='last_approved_purchase_price' ? t('finance.productCost.pricePurchaseFromOrders') : t('finance.productCost.pricePurchase')
  if(type==='operator_input') return t('finance.productCost.priceOperator')
  return type
}
function setOperatorPrice(materialId:unknown, value:string) {
  operatorPrices.value={...operatorPrices.value,[String(materialId)]:value}
}
function setOperatorRate(operationId:unknown, value:string) {
  operatorRates.value={...operatorRates.value,[String(operationId)]:value}
}
function materialPricePayload(version:Version) {
  return Object.entries(operatorPrices.value)
    .filter(([id,value]) => Number(id)>0 && String(value).trim()!=='')
    .map(([id,value]) => ({material_id:Number(id),unit_rate:String(value).trim(),currency_code:version.costing_currency_code}))
}
function operationRatePayload(version:Version) {
  return Object.entries(operatorRates.value)
    .filter(([id,value]) => Number(id)>0 && String(value).trim()!=='')
    .map(([id,value]) => ({operation_id:Number(id),unit_rate:String(value).trim(),currency_code:version.costing_currency_code}))
}
/** Operator prices and rates are editable for every line; entered decimals are kept verbatim. */
function seedOperatorInputs(snapshot:Row | null) {
  const prices:Record<string,string>={...operatorPrices.value}
  for(const row of ((snapshot?.materials as Row[] | undefined) ?? [])) {
    const price = priceOf(row)
    if(String(price?.source_type ?? '')==='operator_input' && price?.unit_rate!=null) prices[String(row.material_id)]=String(price.unit_rate)
  }
  operatorPrices.value=prices
  const rates:Record<string,string>={...operatorRates.value}
  for(const row of ((snapshot?.operations as Row[] | undefined) ?? [])) {
    const rate = rateOf(row)
    if(rate?.rate!=null) rates[String(row.source_id)]=String(rate.rate)
  }
  operatorRates.value=rates
}
function auditValue(field:string):string {
  const value = selected.value?.[field]
  if(field.endsWith('_at') && typeof value==='string' && value) return formatDisplayDateTime(value) || value
  return show(value)
}
const overheadBasisKeys:Record<string,string>={
  direct_material_percent:'basisDirectMaterialPercent',direct_labor_percent:'basisDirectLaborPercent',
  prime_cost_percent:'basisPrimeCostPercent',per_produced_unit:'basisPerProducedUnit',
  per_machine_hour:'basisPerMachineHour',per_labor_hour:'basisPerLaborHour',fixed_per_batch:'basisFixedPerBatch',
}
function overheadBasisLabel(basis:unknown):string {
  const code=String(basis ?? '')
  const key=overheadBasisKeys[code]
  return key ? messageOr(`finance.productCost.overheads.${key}`, code) : code
}
function overheadStatusLabel(status:string):string {
  return messageOr(`finance.productCost.overheads.status${status.charAt(0).toUpperCase()}${status.slice(1)}`, status)
}
function overheadScopeLabel(rule:OverheadRule):string {
  if(rule.product_id != null) return t('finance.productCost.overheads.scopeProduct')
  if(rule.product_group_id != null) return t('finance.productCost.overheads.scopeGroup')
  if(rule.department_agent_id != null) return t('finance.productCost.overheads.scopeDepartment')
  return t('finance.productCost.overheads.scopeAll')
}
/** Rules the collector resolves for this version: scope, department and date window. */
const applicableOverheadRules=computed(()=>{
  const version=selected.value
  if(!version) return []
  const groupId=(sources.value?.bom as Row | null | undefined)?.product_group_id ?? null
  const departments=new Set(bomOperations.value
    .map(row=>row.department_agent_id)
    .filter(id=>id != null && id !== '')
    .map(String))
  const date=String(version.costing_date ?? '')
  return overheadRules.value.filter(rule=>{
    if(rule.approval_status==='retired') return false
    if(rule.product_id != null && String(rule.product_id)!==String(version.product_id)) return false
    if(rule.product_group_id != null && (groupId == null || String(rule.product_group_id)!==String(groupId))) return false
    if(rule.department_agent_id != null && !departments.has(String(rule.department_agent_id))) return false
    if(rule.effective_from && date && rule.effective_from > date) return false
    if(rule.effective_to && date && rule.effective_to < date) return false
    return true
  })
})
async function loadCurrencies() {
  if(currencies.value.length) { ensureOverheadCurrency(); return }
  try { currencies.value=await listCurrencies() } catch { currencies.value=[] }
  ensureOverheadCurrency()
}
/** Amount overhead rules default to UZS, the accounting base currency. */
function defaultCurrencyId():string {
  const uzs=currencies.value.find(currency=>String(currency.code ?? '').toUpperCase()==='UZS')
  const base=currencies.value.find(currency=>Number(currency.is_base)===1)
  return String((uzs ?? base)?.id ?? '')
}
function ensureOverheadCurrency() {
  if(overheadForm.value.rate_type!=='amount' || overheadForm.value.currency_id) return
  const id=defaultCurrencyId()
  if(id) overheadForm.value.currency_id=id
}
watch(()=>overheadForm.value.rate_type,()=>ensureOverheadCurrency())
function overheadCode(expense:ExpenseReference):string {
  const base=String(expense.code || expense.name).toUpperCase().replace(/[^A-Z0-9]+/g,'-').replace(/^-+|-+$/g,'')
  const scope=overheadForm.value.apply_to_product && selected.value ? `-P${selected.value.product_id}` : ''
  return `${base.slice(0,80-scope.length)}${scope}`
}
function chooseExpense(expense:ExpenseReference) {
  expensePicker.value=false
  selectedExpense.value=expense
  overheadForm.value.name=expense.name
  overheadForm.value.code=overheadCode(expense)
  const amount=Number(expense.base_amount ?? 0)
  if(amount>0) {
    overheadForm.value.rate_value=String(expense.base_amount)
    overheadForm.value.rate_type='amount'
  }
  const currencyCode=String(expense.default_rate_currency_code ?? '').toUpperCase()
  const match=currencies.value.find(currency=>String(currency.code ?? '').toUpperCase()===currencyCode)
  if(match) overheadForm.value.currency_id=String(match.id)
  ensureOverheadCurrency()
}
async function loadOverheadRules() {
  if(!can('cost_overheads.view')) { overheadRules.value=[]; return }
  overheadsLoading.value=true
  try { overheadRules.value=await listOverheadRules() } catch(cause) { overheadError.value=formatApiError(cause) }
  finally { overheadsLoading.value=false }
}
function beginOverhead() {
  const version=selected.value
  if(!version) return
  overheadError.value=''
  selectedExpense.value=null
  overheadForm.value={code:'',name:'',allocation_basis:'prime_cost_percent',rate_type:'percent',rate_value:'',
    currency_id:defaultCurrencyId(),apply_to_product:true,effective_from:String(version.costing_date ?? ''),effective_to:'',priority:'0',
    approve:can('cost_overheads.approve')}
  overheadOpen.value=true
  void loadCurrencies()
}
async function saveOverhead() {
  const version=selected.value
  if(!version || overheadSaving.value) return
  overheadError.value=''
  const form=overheadForm.value
  const rate=String(form.rate_value).trim()
  if(!selectedExpense.value || !form.code.trim() || !form.name.trim() || !form.effective_from || !/^\d+(\.\d{1,8})?$/.test(rate) || Number(rate)<=0) {
    overheadError.value=t('finance.productCost.overheads.invalid');return
  }
  if(form.rate_type==='amount' && !form.currency_id) { overheadError.value=t('finance.productCost.overheads.currencyRequired');return }
  overheadSaving.value=true
  try {
    const payload:OverheadRulePayload={
      code:form.code.trim().toUpperCase(),name:form.name.trim(),allocation_basis:form.allocation_basis,rate_type:form.rate_type,
      rate_value:rate,currency_id:form.rate_type==='amount'?Number(form.currency_id):null,
      product_id:form.apply_to_product?version.product_id:null,product_group_id:null,department_agent_id:null,work_center_id:null,
      effective_from:form.effective_from,effective_to:form.effective_to||null,priority:Number(form.priority)||0,
    }
    const created=await createOverheadRule(payload)
    if(form.approve && can('cost_overheads.approve')) await approveOverheadRule(created.id)
    overheadOpen.value=false
    await loadOverheadRules()
    await fetchSources(version)
  } catch(cause) { overheadError.value=formatApiError(cause) } finally { overheadSaving.value=false }
}
async function approveRule(rule:OverheadRule) {
  const version=selected.value
  if(!version || overheadSaving.value) return
  overheadSaving.value=true; overheadError.value=''
  try { await approveOverheadRule(rule.id); await loadOverheadRules(); await fetchSources(version) }
  catch(cause) { overheadError.value=formatApiError(cause) } finally { overheadSaving.value=false }
}
async function run(task: () => Promise<void>) {
  if(busy.value) return
  busy.value=true; error.value=''
  try { await task() } catch(cause) { error.value=formatApiError(cause) } finally { busy.value=false }
}
async function load() {
  const response=await client.get<{data:Version[]}>('/product-costs',{params:{product_id:filter.value || undefined,limit:50,offset:offset.value}})
  versions.value=response.data.data
}
async function open(id: number) {
  await run(async () => {
    selected.value=null; sources.value=null; comparison.value=null
    selected.value=(await client.get<{data:Version}>(`/product-costs/${id}`)).data.data
    await loadComparisonOptions(selected.value)
    await fetchSources(selected.value)
    await loadOverheadRules()
  })
}
async function mutate(action: string) {
  const version=selected.value
  if(!version) return
  if(action==='publish' && !window.confirm(t('finance.productCost.publishConfirm'))) return
  if(action==='calculate' && !window.confirm(t('finance.productCost.calculateConfirm'))) return
  await run(async () => {
    const body:Row={row_version:version.row_version}
    if(action==='calculate') {
      if(!/^\d+(\.\d{1,8})?$/.test(pricingValue.value) || (pricingMethod.value==='target_margin' && Number(pricingValue.value)>=100)) {
        throw new Error(t('finance.productCost.invalidPricing'))
      }
      body.costing_currency_id=version.costing_currency_id
      body.pricing={method:pricingMethod.value,value:pricingValue.value,currency_id:version.costing_currency_id}
      const prices=materialPricePayload(version)
      if(prices.length) body.material_prices=prices
      const rates=operationRatePayload(version)
      if(rates.length) body.operation_rates=rates
    }
    const identity=JSON.stringify([version.id,action,body])
    const key=retryKeys.get(identity) ?? crypto.randomUUID()
    retryKeys.set(identity,key)
    const response=await client.post<{data:Version | {version:Version}}>(`/product-costs/${version.id}/${action}`,body,{headers:{'Idempotency-Key':key}})
    const result=response.data.data
    selected.value=action==='calculate' ? (result as {version:Version}).version : result as Version
    retryKeys.delete(identity); sources.value=null; comparison.value=null
    await load()
    if(selected.value) await fetchSources(selected.value)
  })
}
async function fetchSources(version:Version) {
  sources.value=(await client.post<{data:Row}>(`/product-costs/${version.id}/collect-sources`,
    {row_version:version.row_version, material_prices:materialPricePayload(version), operation_rates:operationRatePayload(version)})).data.data
  seedOperatorInputs(sources.value)
}
async function collect() {
  if(!selected.value) return
  const version=selected.value
  await run(async () => { await fetchSources(version) })
}
async function compare() {
  if(!selected.value || !compareId.value) return
  const left=selected.value
  const right=comparisonOptions.value.find(version=>String(version.id)===compareId.value)
  if(!right || !comparable(left,right)) return
  await run(async () => {
    comparison.value=null
    const result=(await client.get<{data:{left:Version;right:Version;unit_cost_delta:string}}>('/product-costs/compare',{params:{left_id:left.id,right_id:right.id}})).data.data
    if(!comparable(result.left,result.right)) throw new Error(t('finance.productCost.incompatibleVersions'))
    comparison.value=result
  })
}
onMounted(() => run(load))
</script>

<template>
  <main class="cost-workspace" :aria-busy="busy">
    <header class="page-heading"><RouterLink class="back" to="/costing">← {{ t('finance.productCost.backToCosting') }}</RouterLink><h2>{{ t('finance.productCost.title') }}</h2><p class="empty-inline">{{ t('finance.productCost.subtitle') }}</p></header>
    <p v-if="error" role="alert" class="notice error">{{ error }} <button class="btn-secondary" :disabled="busy" @click="run(load)">{{ t('finance.productCost.refreshList') }}</button></p>
    <form class="toolbar" @submit.prevent="offset=0; run(load)">
      <button class="btn-secondary" type="button" :disabled="busy || createOpen || !can('products.view')" @click="filterPicker=true">{{ filterLabel }}</button>
      <button class="btn-secondary" v-if="filter" type="button" :disabled="busy || createOpen" @click="filter='';filterName='';offset=0;selected=null;run(load)">{{ t('finance.productCost.allProducts') }}</button>
      <button class="btn-secondary" :disabled="busy">{{ t('finance.productCost.find') }}</button>
      <button class="btn-primary" v-if="can('product_costs.create')" type="button" :disabled="busy || createOpen" @click="beginEditor('create')">{{ t('finance.productCost.newDraft') }}</button>
    </form>
    <ProductCostDraftEditor v-if="createOpen" :key="editorMode+':'+(editorVersion?.id??'new')" :mode="editorMode" :version="editorVersion" @saved="saved" @cancel="createOpen=false" />
    <ProductPickerModal :open="filterPicker" :selected-id="filter" @close="filterPicker=false" @select="chooseFilter" />
    <OverheadPickerModal :open="expensePicker" :selected-id="selectedExpense?.id ?? null" @close="expensePicker=false" @select="chooseExpense" />
    <div class="layout">
      <aside class="panel">
        <h3>{{ t('finance.productCost.versions') }}</h3><p v-if="!versions.length && !busy">{{ t('finance.productCost.noVersions') }}</p>
        <button v-for="version in versions" :key="version.id" class="btn-secondary version" :aria-pressed="selected?.id===version.id" :disabled="busy || createOpen" @click="open(version.id)">
          {{ version.product_name ?? '—' }} · v{{ version.version_number }}<small><span class="status-badge status-neutral">{{ statusLabel(version.status) }}</span> · {{ version.costing_currency_code }}</small>
        </button>
        <div class="toolbar"><button class="btn-secondary" :disabled="busy || offset===0" @click="offset=Math.max(0,offset-50); run(load)">{{ t('finance.common.previous') }}</button><button class="btn-secondary" :disabled="busy || versions.length<50" @click="offset+=50; run(load)">{{ t('finance.common.next') }}</button></div>
      </aside>
      <section v-if="selected" class="panel detail">
        <div class="panel-header"><h3>{{ t('finance.productCost.versionHeading', { product: selectedProductName || '—', version: selected.version_number }) }}</h3><span class="status-badge" :class="selected.status==='published'?'status-ok':selected.status==='rejected'?'status-critical':'status-info'">{{ statusLabel(selected.status) }}</span></div>
        <p>{{ formatDisplayDate(String(selected.costing_date ?? '')) }} · {{ selected.costing_currency_code }} · BOM {{ selected.bom_version_id }} · {{ selected.shift }}</p>
        <div class="summary-row"><div class="summary-card"><span class="summary-label">{{ t('finance.productCost.batch') }}</span><span class="summary-value">{{ formatQuantity(selected.batch_quantity) }}</span></div><div class="summary-card accent"><span class="summary-label">{{ t('finance.productCost.expectedOutput') }}</span><span class="summary-value">{{ formatQuantity(selected.expected_output_quantity) }}</span></div><div class="summary-card"><span class="summary-label">{{ t('finance.productCost.yield') }}</span><span class="summary-value">{{ formatQuantity(selected.yield_rate) }}</span></div></div>
        <div class="toolbar">
          <button class="btn-secondary" v-if="can('product_costs.calculate')" :disabled="busy || createOpen" @click="collect">{{ t('finance.productCost.checkSources') }}</button>
          <button class="btn-secondary" v-if="can('product_costs.create') && ['draft','rejected'].includes(selected.status)" :disabled="busy || createOpen" @click="beginEditor('edit')">{{ t('finance.productCost.edit') }}</button>
          <button class="btn-secondary" v-if="can('product_costs.create')" :disabled="busy || createOpen" @click="beginEditor('clone')">{{ t('finance.productCost.clone') }}</button>
          <button class="action-button" :class="action.id==='reject' ? 'btn-danger' : 'btn-primary'" v-for="action in actions" :key="action.id" :disabled="busy || createOpen" @click="mutate(action.id)">{{ action.label }}</button>
          <button class="btn-secondary" :disabled="busy || createOpen" @click="open(selected.id)">{{ t('finance.productCost.refreshVersion') }}</button>
        </div>
        <fieldset v-if="can('product_costs.calculate') && ['draft','calculated'].includes(selected.status)" :disabled="busy || createOpen" class="toolbar">
          <legend>{{ t('finance.productCost.pricingLegend') }}</legend>
          <label class="field">{{ t('finance.productCost.pricingMethod') }} <select class="filter-select" v-model="pricingMethod"><option value="markup">{{ t('finance.productCost.pricing.markup') }}</option><option value="target_margin">{{ t('finance.productCost.pricing.targetMargin') }}</option><option value="manual">{{ t('finance.productCost.pricing.manual') }}</option></select></label>
          <label class="field">{{ pricingMethod==='manual' ? selected.costing_currency_code : '%' }} <input class="search-input" v-model="pricingValue" inputmode="decimal"></label>
          <p>{{ t('finance.productCost.pricingNote') }}</p>
        </fieldset>
        <section v-if="selected && sources" class="panel bom">
          <div class="panel-header"><h3>{{ t('finance.productCost.bomComposition') }}</h3><span class="status-badge status-neutral">{{ t('finance.productCost.bomSummary', { materials: bomMaterials.length, operations: bomOperations.length }) }}</span></div>
          <p>{{ t('finance.productCost.bomNote') }}</p>
          <div class="table-scroll"><table class="data-table">
            <thead><tr><th>{{ t('finance.productCost.material') }}</th><th class="cell-num">{{ t('finance.productCost.qtyPerBatch') }}</th><th>{{ t('finance.productCost.unit') }}</th><th class="cell-num">{{ t('finance.productCost.sourcePrice') }}</th><th>{{ t('finance.productCost.priceSource') }}</th><th>{{ t('finance.productCost.operatorPrice') }}</th></tr></thead>
            <tbody>
              <tr v-for="row in bomMaterials" :key="'m'+String(row.material_id)">
                <td>{{ row.name }}<small>{{ t('finance.productCost.materialMeta', { sku: row.sku ?? '—', sequence: row.operation_sequence }) }}</small></td>
                <td class="cell-num">{{ formatQuantity(row.net_quantity) }}</td>
                <td>{{ uomOf(row)?.uom_code ?? '—' }}</td>
                <td class="cell-num">{{ formatMoneyValue(priceOf(row)?.unit_rate) }} {{ priceOf(row)?.currency_code ?? '' }}</td>
                <td>{{ priceSourceLabel(row) }}</td>
                <td><input class="search-input" inputmode="decimal" :disabled="busy"
                  :value="operatorPrices[String(row.material_id)] ?? ''"
                  :placeholder="String(priceOf(row)?.source_type ?? '')==='product_purchase_price' ? t('finance.productCost.purchaseInUse') : t('finance.productCost.enterPrice')"
                  @input="setOperatorPrice(row.material_id, ($event.target as HTMLInputElement).value)">
                  <small v-if="priceOverrideNotice(row)" class="cell-warning">{{ priceOverrideNotice(row) }}</small></td>
              </tr>
            </tbody>
          </table></div>
          <p v-if="!bomMaterials.length">{{ t('finance.productCost.noBomMaterials') }}</p>
          <div class="table-scroll"><table class="data-table">
            <thead><tr><th>{{ t('finance.productCost.operation') }}</th><th class="cell-num">{{ t('finance.productCost.durationMinutes') }}</th><th class="cell-num">{{ t('finance.productCost.rate') }}</th><th>{{ t('finance.productCost.rateSource') }}</th><th>{{ t('finance.productCost.operatorRate') }}</th></tr></thead>
            <tbody>
              <tr v-for="row in bomOperations" :key="'o'+String(row.source_id)">
                <td>{{ row.operation_sequence }} · {{ row.name }}</td>
                <td class="cell-num">{{ formatQuantity(row.duration_minutes) }}</td>
                <td class="cell-num">{{ formatMoneyValue(rateOf(row)?.rate) }}</td>
                <td>{{ rateOf(row)?.source_type ?? t('finance.productCost.rateMissing') }}</td>
                <td><input class="search-input" inputmode="decimal" :disabled="busy"
                  :value="operatorRates[String(row.source_id)] ?? ''"
                  :placeholder="t('finance.productCost.enterRate')"
                  @input="setOperatorRate(row.source_id, ($event.target as HTMLInputElement).value)"></td>
              </tr>
            </tbody>
          </table></div>
          <p v-if="!bomOperations.length">{{ t('finance.productCost.noBomOperations') }}</p>
          <p>{{ t('finance.productCost.operatorPricesNote') }}</p>
        </section>
        <nav class="tabs" :aria-label="t('finance.productCost.tabsLabel')"><button class="tab-button" :class="{ 'is-active': tab===item.id }" v-for="item in tabs" :key="item.id" :aria-pressed="tab===item.id" @click="tab=item.id">{{ item.label }}<span v-if="item.badge" class="tab-count">{{ item.badge }}</span></button></nav>
        <template v-if="!['warnings','summary','audit'].includes(tab)">
          <section v-if="tab==='expenses'" class="panel overheads">
            <div class="panel-header"><h3>{{ t('finance.productCost.overheads.title') }}</h3><button v-if="can('cost_overheads.manage')" class="btn-primary" type="button" :disabled="busy || createOpen || overheadSaving" @click="beginOverhead">{{ t('finance.productCost.overheads.add') }}</button></div>
            <p>{{ t('finance.productCost.overheads.help') }}</p>
            <p v-if="!can('cost_overheads.view')">{{ t('finance.productCost.overheads.noPermission') }}</p>
            <p v-else-if="overheadsLoading">{{ t('finance.productCost.overheads.loading') }}</p>
            <p v-else-if="!overheadRules.length">{{ t('finance.productCost.overheads.empty') }}</p>
            <div v-else-if="applicableOverheadRules.length" class="table-scroll"><table class="data-table">
              <thead><tr><th>{{ t('finance.productCost.overheads.code') }}</th><th>{{ t('finance.productCost.overheads.name') }}</th><th>{{ t('finance.productCost.overheads.basis') }}</th><th class="cell-num">{{ t('finance.productCost.overheads.rateValue') }}</th><th>{{ t('finance.productCost.overheads.scope') }}</th><th>{{ t('finance.common.status') }}</th><th></th></tr></thead>
              <tbody><tr v-for="rule in applicableOverheadRules" :key="rule.id">
                <td class="cell-code">{{ rule.code }}</td>
                <td>{{ rule.name }}</td>
                <td>{{ overheadBasisLabel(rule.allocation_basis) }}</td>
                <td class="cell-num">{{ rule.rate_type==='percent' ? `${formatMoneyValue(rule.rate_value)}%` : `${formatMoneyValue(rule.rate_value)} ${rule.currency_code ?? ''}` }}</td>
                <td>{{ overheadScopeLabel(rule) }}</td>
                <td><span class="status-badge" :class="rule.approval_status==='approved'?'status-ok':rule.approval_status==='retired'?'status-critical':'status-warn'">{{ overheadStatusLabel(rule.approval_status) }}</span></td>
                <td class="cell-actions"><button v-if="rule.approval_status==='draft' && can('cost_overheads.approve')" class="btn-secondary" type="button" :disabled="overheadSaving || busy" @click="approveRule(rule)">{{ t('finance.productCost.overheads.approve') }}</button></td>
              </tr></tbody>
            </table></div>
            <p v-else>{{ t('finance.productCost.overheads.noneApplicable') }}</p>
            <form v-if="overheadOpen" class="overhead-form" @submit.prevent="saveOverhead">
              <div class="form-grid tight">
                <label class="field">{{ t('finance.productCost.overheads.expense') }} <button class="field-button" type="button" :disabled="!can('products.view')" @click="expensePicker=true">{{ selectedExpense?.name || t('finance.productCost.overheads.selectExpense') }}</button></label>
                <label class="field">{{ t('finance.productCost.overheads.code') }}<input v-model="overheadForm.code" required maxlength="80"></label>
                <label class="field">{{ t('finance.productCost.overheads.basis') }}<select v-model="overheadForm.allocation_basis"><option value="prime_cost_percent">{{ t('finance.productCost.overheads.basisPrimeCostPercent') }}</option><option value="direct_material_percent">{{ t('finance.productCost.overheads.basisDirectMaterialPercent') }}</option><option value="direct_labor_percent">{{ t('finance.productCost.overheads.basisDirectLaborPercent') }}</option><option value="per_produced_unit">{{ t('finance.productCost.overheads.basisPerProducedUnit') }}</option><option value="per_machine_hour">{{ t('finance.productCost.overheads.basisPerMachineHour') }}</option><option value="per_labor_hour">{{ t('finance.productCost.overheads.basisPerLaborHour') }}</option><option value="fixed_per_batch">{{ t('finance.productCost.overheads.basisFixedPerBatch') }}</option></select></label>
                <label class="field">{{ t('finance.productCost.overheads.rateType') }}<select v-model="overheadForm.rate_type"><option value="percent">{{ t('finance.productCost.overheads.percent') }}</option><option value="amount">{{ t('finance.productCost.overheads.amount') }}</option></select></label>
                <label class="field">{{ t('finance.productCost.overheads.rateValue') }}<input v-model="overheadForm.rate_value" inputmode="decimal" required></label>
                <label v-if="overheadForm.rate_type==='amount'" class="field">{{ t('finance.productCost.overheads.currency') }}<select v-model="overheadForm.currency_id" required><option value="" disabled>—</option><option v-for="currency in currencies" :key="currency.id" :value="String(currency.id)">{{ currency.code }} — {{ currency.name }}</option></select></label>
                <label class="field">{{ t('finance.productCost.overheads.effectiveFrom') }}<input type="date" v-model="overheadForm.effective_from" required></label>
                <label class="field">{{ t('finance.productCost.overheads.effectiveTo') }}<input type="date" v-model="overheadForm.effective_to"></label>
                <label class="field">{{ t('finance.productCost.overheads.priority') }}<input v-model="overheadForm.priority" inputmode="numeric"></label>
              </div>
              <label class="check-field"><input type="checkbox" v-model="overheadForm.apply_to_product"> {{ t('finance.productCost.overheads.scopeProduct') }}</label>
              <label v-if="can('cost_overheads.approve')" class="check-field"><input type="checkbox" v-model="overheadForm.approve"> {{ t('finance.productCost.overheads.approveNow') }}</label>
              <p v-if="overheadError" role="alert" class="notice error">{{ overheadError }}</p>
              <div class="form-actions"><button class="btn-primary" :disabled="overheadSaving">{{ t('finance.productCost.overheads.save') }}</button><button class="btn-secondary" type="button" @click="overheadOpen=false">{{ t('finance.productCost.overheads.cancel') }}</button></div>
            </form>
            <p v-else-if="overheadError" role="alert" class="notice error">{{ overheadError }}</p>
          </section>
          <div class="table-scroll"><table class="data-table"><thead><tr><th>{{ t('finance.productCost.lines.item') }}</th><th class="cell-num">{{ t('finance.productCost.lines.quantity') }}</th><th class="cell-num">{{ t('finance.productCost.lines.sourceRate') }}</th><th class="cell-num">{{ t('finance.productCost.lines.currencyId') }}</th><th class="cell-num">{{ t('finance.productCost.lines.fx') }}</th><th class="cell-num">{{ t('finance.productCost.lines.costRate', { currency: selected.costing_currency_code }) }}</th><th class="cell-num">{{ t('finance.productCost.lines.amount') }}</th></tr></thead>
          <tbody><tr v-for="line in rows" :key="String(line.id)"><td>{{ line.name }}<small>{{ line.source_type }} #{{ line.source_id }}</small><details><summary>{{ t('finance.productCost.lines.origin') }}</summary><pre>{{ show(line.resolution_metadata) }}</pre></details></td><td class="cell-num">{{ formatQuantity(line.quantity) }}</td><td class="cell-num">{{ formatMoneyValue(line.source_unit_rate) }}</td><td class="cell-num">{{ line.source_currency_id }}</td><td class="cell-num">{{ formatMoneyValue(line.fx_rate) }}</td><td class="cell-num">{{ formatMoneyValue(line.costing_unit_rate) }}</td><td class="cell-num">{{ formatMoneyValue(line.amount) }}</td></tr></tbody></table></div>
          <p v-if="!rows.length">{{ t('finance.productCost.noLines') }}</p>
          <p>{{ t('finance.productCost.overridesGap') }}</p>
        </template>
        <template v-else-if="tab==='warnings'">
          <div v-if="!sources" class="state-message"><p>{{ t('finance.productCost.warningsPrompt') }}</p><button v-if="can('product_costs.calculate')" class="btn-secondary" :disabled="busy || createOpen" @click="collect">{{ t('finance.productCost.checkSources') }}</button></div>
          <template v-else>
            <p>{{ t('finance.productCost.calculable') }}: {{ sources.is_calculable ? t('finance.common.yes') : t('finance.common.no') }}</p>
            <p v-if="!issues.length">{{ t('finance.productCost.noIssues') }}</p>
            <article v-for="(issue,index) in issues" :key="index" class="notice" :class="{error:issue.blocking}">
              <strong>{{ issue.blocking ? t('finance.productCost.blocking') : t('finance.productCost.warning') }} · {{ issue.code }}</strong>
              <p>{{ issue.message }}</p><p>{{ guidanceFor(String(issue.code)) }}</p>
              <details><summary>{{ t('finance.productCost.details') }}</summary><pre>{{ show(issue) }}</pre></details>
            </article>
          </template>
        </template>
        <template v-else-if="tab==='summary'">
          <h3>{{ t('finance.productCost.serverTotals', { currency: selected.costing_currency_code }) }}</h3>
          <table class="data-table"><thead><tr><th>{{ t('finance.productCost.totals.item') }}</th><th class="cell-num">{{ t('finance.productCost.totals.perBatch') }}</th><th class="cell-num">{{ t('finance.productCost.totals.perUnit') }}</th></tr></thead><tbody><tr v-for="total in selected.totals" :key="String(total.cost_element)"><td>{{ costElementLabel(total.cost_element) }}</td><td class="cell-num">{{ formatMoneyValue(total.batch_amount) }}</td><td class="cell-num">{{ formatMoneyValue(total.unit_amount) }}</td></tr></tbody></table>
          <p v-if="serverMissingKnitting">{{ t('finance.productCost.serverGroupingNote') }}</p>
          <h3>{{ t('finance.productCost.proposedPrices') }}</h3>
          <table class="data-table"><thead><tr><th>{{ t('finance.productCost.prices.method') }}</th><th class="cell-num">{{ t('finance.productCost.prices.value') }}</th><th class="cell-num">{{ t('finance.productCost.prices.unitPrice') }}</th></tr></thead><tbody><tr v-for="price in selected.prices" :key="String(price.id)"><td>{{ pricingMethodLabel(price.pricing_method) }}</td><td class="cell-num">{{ formatMoneyValue(price.pricing_value) }}</td><td class="cell-num">{{ formatMoneyValue(price.proposed_unit_price) }} {{ selected.costing_currency_code }}</td></tr></tbody></table>
          <p>{{ t('finance.productCost.publishGap') }}</p>
          <form class="toolbar" @submit.prevent="compare"><label class="field">{{ t('finance.productCost.comparableVersion') }} <select class="filter-select" v-model="compareId" required><option value="" disabled>{{ t('finance.productCost.selectVersion') }}</option><option v-for="version in comparisonOptions" :key="version.id" :value="String(version.id)">v{{ version.version_number }} · {{ statusLabel(version.status) }} · {{ formatDisplayDate(String(version.costing_date ?? '')) }}</option></select></label><button class="btn-secondary" :disabled="busy || !compareId">{{ t('finance.productCost.compare') }}</button></form>
          <p>{{ t('finance.productCost.comparisonHint') }}</p>
          <section v-if="comparison">
            <h3>v{{ comparison.left.version_number }} → v{{ comparison.right.version_number }}</h3>
            <p>{{ t('finance.productCost.unitCostDelta') }}: {{ formatMoneyValue(comparison.unit_cost_delta) }} {{ selected.costing_currency_code }}</p>
            <div class="table-scroll"><table class="data-table"><thead><tr><th>{{ t('finance.productCost.comparison.item') }}</th><th class="cell-num">{{ t('finance.productCost.comparison.quantityLeft') }}</th><th class="cell-num">{{ t('finance.productCost.comparison.quantityRight') }}</th><th class="cell-num">{{ t('finance.productCost.comparison.amountLeft') }}</th><th class="cell-num">{{ t('finance.productCost.comparison.amountRight') }}</th></tr></thead>
              <tbody><tr v-for="row in comparisonRows" :key="row.key"><td>{{ row.left?.name || row.right?.name }}<small>{{ !row.left ? t('finance.productCost.added') : !row.right ? t('finance.productCost.removed') : '' }}</small></td><td class="cell-num">{{ formatQuantity(row.left?.quantity) }}</td><td class="cell-num">{{ formatQuantity(row.right?.quantity) }}</td><td class="cell-num">{{ formatMoneyValue(row.left?.amount) }}</td><td class="cell-num">{{ formatMoneyValue(row.right?.amount) }}</td></tr></tbody>
            </table></div>
          </section>
        </template>
        <template v-else><h3>{{ t('finance.productCost.approvalData') }}</h3><dl v-for="field in auditFields" :key="field.field"><dt>{{ t('finance.productCost.auditFields.' + field.key) }}</dt><dd>{{ auditValue(field.field) }}</dd></dl><p>{{ t('finance.productCost.approvalGap') }}</p></template>
      </section>
      <section v-else class="panel"><p>{{ t('finance.productCost.selectVersionPrompt') }}</p></section>
    </div>
  </main>
</template>
<style scoped>
.cost-workspace { min-width: 0; }
.layout { display: grid; grid-template-columns: 260px minmax(0, 1fr); gap: 16px; }
.layout > *, .detail { min-width: 0; }
.version { display: block; width: 100%; text-align: left; margin-bottom: 8px; }
.version[aria-pressed="true"] { border-color: #4e6651; background: #f4f8f5; }
.version small { display: block; margin-top: 6px; }
.detail > .table-scroll, .detail > table { margin-bottom: 16px; }
.detail > fieldset { padding: 0; border: 0; min-width: 0; }
.detail > fieldset legend { margin-bottom: 10px; font-size: 13px; font-weight: 700; }
.sources { margin-bottom: 20px; }
.bom { margin-bottom: 20px; }
.bom .search-input { min-width: 120px; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; font-size: 12px; max-height: 350px; overflow: auto; }
td small { display: block; margin-top: 4px; }
.cell-warning { color: #b45309; font-weight: 400; line-height: 1.35; white-space: normal; max-width: 230px; }
.tab-count { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; margin-left: 6px; padding: 0 5px; border-radius: 999px; background: #b45309; color: #fff; font-size: 11px; font-weight: 700; }
.overheads { margin-bottom: 16px; }
.overheads > p { margin: 0 0 10px; }
.overhead-form { margin-top: 14px; padding-top: 14px; border-top: 1px solid #e0e6e1; }
.check-field { display: flex; align-items: center; gap: 8px; margin-top: 10px; color: #4c5a4f; font-size: 13px; font-weight: 600; }
.check-field input { width: auto; }
dl { display: grid; grid-template-columns: minmax(150px, 1fr) 2fr; gap: 10px; font-size: 13px; }
dd { margin: 0; overflow-wrap: anywhere; }
@media (max-width: 1024px) { .layout { grid-template-columns: 1fr; } }
</style>
